import { Link, useLocation } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { Phone, MessageCircle, Menu, X, Instagram } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import { cn } from "@/lib/utils";
import logo from "@/assets/blvds-logo-160.jpg";
import logoWebp from "@/assets/blvds-logo-160.webp";
import logoAvif from "@/assets/blvds-logo-160.avif";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/book", label: "Book" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <picture>
              <source type="image/avif" srcSet={logoAvif} />
              <source type="image/webp" srcSet={logoWebp} />
              <img
                src={logo}
                alt="BLVDS Detailing"
                className="h-10 w-auto object-contain"
                width={160}
                height={160}
                fetchPriority="high"
                decoding="async"
              />
            </picture>
            <span className="sr-only">BLVDS Detailing</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md transition-smooth",
                  location.pathname === item.to
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={`tel:${BUSINESS.phone}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:border-primary/50 transition-smooth text-sm"
            >
              <Phone className="w-4 h-4 text-primary" />
              {BUSINESS.phoneDisplay}
            </a>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-primary text-primary-foreground font-semibold text-sm hover:shadow-glow transition-smooth"
            >
              Book Now
            </Link>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center w-12 h-12 -mr-2 rounded-md"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-2">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center min-h-12 px-4 py-3 rounded-md text-base font-medium",
                    location.pathname === item.to
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-surface"
                  )}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/book"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center w-full min-h-12 mt-3 px-4 py-3 rounded-lg bg-gradient-primary text-primary-foreground font-semibold"
              >
                Book Now
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border/50 bg-surface/50 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <picture>
              <source type="image/avif" srcSet={logoAvif} />
              <source type="image/webp" srcSet={logoWebp} />
              <img src={logo} alt="BLVDS Detailing" className="h-12 w-auto object-contain mb-4" width={160} height={160} loading="lazy" decoding="async" />
            </picture>
            <p className="text-muted-foreground text-sm max-w-md">
              Premium mobile car washing & detailing serving {BUSINESS.serviceArea}. We come to you.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-3 text-sm">Contact</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <a
                  href={`tel:${BUSINESS.phone}`}
                  className="inline-flex items-center min-h-12 py-2 hover:text-primary"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="inline-flex items-center min-h-12 py-2 hover:text-primary break-all"
                >
                  {BUSINESS.email}
                </a>
              </li>
              <li className="pt-1">{BUSINESS.serviceArea}</li>
            </ul>
            <div className="flex items-center gap-3 mt-4">
              <a
                href={BUSINESS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-12 h-12 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-smooth"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={BUSINESS.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="w-12 h-12 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-smooth"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.71a8.16 8.16 0 0 0 4.77 1.52V6.79a4.85 4.85 0 0 1-1.84-.1z"/>
                </svg>
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-3 text-sm">Quick Links</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="inline-flex items-center min-h-12 py-2 hover:text-primary">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-border/50 py-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} BLVDS Detailing. All rights reserved.
        </div>
      </footer>

      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-4">
        <a
          href={`sms:${BUSINESS.phone}?&body=Hi%20BLVDS%2C%20I%27d%20like%20to%20book%20a%20detail.`}
          className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow hover:scale-110 transition-smooth"
          aria-label="Text us"
        >
          <MessageCircle className="w-6 h-6 text-primary-foreground" />
        </a>
        <a
          href={`tel:${BUSINESS.phone}`}
          className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow hover:scale-110 transition-smooth"
          aria-label="Call"
        >
          <Phone className="w-6 h-6 text-primary-foreground" />
        </a>
      </div>
    </div>
  );
}
